import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LogOut,
  Shield,
  Home,
  FileText,
  PanelLeft,
  Layers,
  Building2,
  Bell,
} from 'lucide-react';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import { Button } from './ui/button';
import OneVerseLogo from './OneVerseLogo';
import { useUser } from '@/providers/UserProvider';
import { logout } from '@/lib/logout';
import { useSidebarCollapse } from '@/contexts/SidebarCollapseContext';
import { cn } from '@/lib/utils';
import PoweredBy from './PoweredBy';
import LocaleSwitcher from './LocaleSwitcher';

const AppSidebar: React.FC = () => {
  const { userData, loading: userLoading } = useUser();
  const { pathname } = useLocation();
  const { isCollapsed, toggleSidebar } = useSidebarCollapse();
  const { t } = useTranslation();
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  const canAccessAdmin = userLoading
    ? null
    : (userData?.can_access_admin ?? false);

  const menuItems = [
    { href: '/dashboard', label: t('nav.home'), icon: Home, isExternal: false },
    {
      href: '/studies',
      label: t('nav.studies'),
      icon: Layers,
      isExternal: false,
    },
    {
      href: '/sites',
      label: t('nav.sites'),
      icon: Building2,
      isExternal: false,
    },
    {
      href: '/submissions',
      label: t('nav.submissions'),
      icon: FileText,
      isExternal: false,
    },
    // Only show admin menu item if user has IRB roles
    ...(canAccessAdmin
      ? [
          {
            href: '/admin',
            label: t('nav.admin'),
            icon: Shield,
            isExternal: true,
          },
        ]
      : []),
    // Fully dormant pre-launch: absent (not just disabled) until Hub turns
    // NOTIFICATION_PERSONALIZATION_ENABLED on for this tenant.
    ...(userData?.notification_personalization_enabled
      ? [
          {
            href: '/settings/notifications',
            label: t('nav.notifications'),
            icon: Bell,
            isExternal: false,
          },
        ]
      : []),
  ];

  return (
    <div
      ref={sidebarRef}
      data-testid="sidebar-wrapper"
      data-collapsed={isCollapsed ? 'true' : 'false'}
      className="sidebar-wrapper"
    >
      <Sidebar
        collapsible="icon"
        className={cn(
          "transition-all duration-300 border-gray-200 [&_[data-sidebar='sidebar']]:[background:var(--color-sidebar-bg,#FAFAFA)] [&_[data-sidebar='sidebar']]:shadow-none",
          isCollapsed ? '!w-20' : '!w-[250px]'
        )}
        data-testid="sidebar"
      >
        <SidebarHeader
          className={cn(
            'group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:mt-1',
            isCollapsed && 'mb-6',
            !isCollapsed && 'px-6 py-2'
          )}
        >
          <div
            className={cn(
              'group/header flex items-center gap-4',
              isCollapsed ? 'justify-center px-0' : 'justify-between'
            )}
          >
            {!isCollapsed && (
              <OneVerseLogo
                className="transition-all duration-300 w-40"
                isCollapsed={false}
              />
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className={cn(
                'h-9 w-9 shrink-0 rounded-[10px]',
                'bg-gray-100 hover:bg-gray-200',
                'text-gray-600 hover:text-gray-900',
                'opacity-0 group-hover/header:opacity-100 transition-opacity duration-150',
                isCollapsed && 'opacity-100'
              )}
              aria-label={
                isCollapsed
                  ? t('nav.expand_sidebar')
                  : t('nav.collapse_sidebar')
              }
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
          </div>
        </SidebarHeader>

        <SidebarContent className={cn(!isCollapsed && 'px-4 pt-4')}>
          <SidebarMenu>
            {menuItems.map(({ href, label, icon: Icon, isExternal }, index) => (
              <SidebarMenuItem
                key={href}
                className={cn(isCollapsed ? 'px-2' : 'px-0')}
              >
                <SidebarMenuButton
                  asChild
                  data-active={pathname.startsWith(href)}
                  tooltip={isCollapsed ? label : undefined}
                  className="!p-0 !h-auto hover:!bg-transparent active:!bg-transparent data-[active=true]:!bg-transparent data-[active=true]:!font-normal"
                >
                  <div className="flex w-full">
                    {isExternal ? (
                      <a
                        href={href}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors cursor-pointer text-sm',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                          pathname.startsWith(href)
                            ? 'bg-white text-gray-900 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-white/10 hover:!text-inherit',
                          index > 0 && 'mt-2',
                          isCollapsed && 'justify-center'
                        )}
                      >
                        <Icon className="w-5 h-5" />
                        {!isCollapsed && <span>{label}</span>}
                      </a>
                    ) : (
                      <Link
                        to={href}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors cursor-pointer text-sm',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                          pathname.startsWith(href)
                            ? 'bg-white text-gray-900 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-white/10 hover:!text-inherit',
                          index > 0 && 'mt-2',
                          isCollapsed && 'justify-center'
                        )}
                      >
                        <Icon className="w-5 h-5" />
                        {!isCollapsed && <span>{label}</span>}
                      </Link>
                    )}
                  </div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarContent>

        <SidebarFooter
          className={cn(
            'border-t border-gray-200 items-start',
            !isCollapsed && 'px-4 py-2'
          )}
        >
          <button
            className={cn(
              'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm [color:var(--color-sidebar-text,#374151)] hover:bg-white/10 hover:!text-inherit transition-colors cursor-pointer',
              isCollapsed && 'justify-center'
            )}
            onClick={logout}
          >
            <LogOut size={20} className="opacity-70" />
            {!isCollapsed && <span>{t('nav.logout')}</span>}
          </button>
          {!isCollapsed && (
            <div className="px-3 pb-2 space-y-1 [color:var(--color-sidebar-text,#374151)]">
              <LocaleSwitcher variant="sidebar" />
              <PoweredBy variant="sidebar" />
            </div>
          )}
        </SidebarFooter>
      </Sidebar>
    </div>
  );
};

export default AppSidebar;
